import React, { useState } from 'react';
import { KeyRound, Mail, LogIn, AlertCircle, Eye, EyeOff } from 'lucide-react';
import Button from '../../components/Button';
import Card from '../../components/Card';
import OtpVerification from './OtpVerification';
import { useAuth } from '../../context/AuthContext';
import { UI_TEXT } from '../../config/ui-text';
import { apiErrorMessage } from '../../utils/apiErrorMessage';

interface LoginProps {
  onSwitch: () => void;
  onSuccess: (email: string) => void;
}

const Login: React.FC<LoginProps> = ({ onSwitch, onSuccess }) => {
  const { login, verifyOtp, loginWithCode } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showOtpVerification, setShowOtpVerification] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  // Which flow the shared code screen belongs to. `signup` is the existing unverified-account path;
  // `login` is signing in with an emailed code. One screen, two purposes, so the returning user sees the
  // component they already know instead of a second one that looks almost the same.
  const [codePurpose, setCodePurpose] = useState<'signup' | 'login'>('signup');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    
    try {
      const result = await login(email, password);
      // A marketplace account signing in here is signed in fine (shared session) but
      // belongs on the marketplace. A full page load, not a router navigate: the
      // marketplace has its own auth context, which reads the session on mount.
      if (result.redirect) {
        window.location.href = result.redirect;
        return;
      }
      if (result.success) {
        onSuccess(email);
      } else {
        // Check if this requires OTP verification
        if (result.requiresVerification) {
          setUnverifiedEmail(email);
          setShowOtpVerification(true);
        } else {
          setError(result.message);
        }
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const apiBase = import.meta.env.VITE_API_URL || '';

  /** Ask the backend to email a sign-in code. */
  const handleRequestLoginCode = async () => {
    if (!email.trim()) {
      // The backend answers identically for every address, so it cannot tell us an email is missing —
      // that check has to live here or the user gets a "code sent" message having typed nothing.
      setError('Enter your email address first.');
      return;
    }
    setError(null);
    setIsLoading(true);
    try {
      const response = await fetch(`${apiBase}/request-login-code`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        setError(apiErrorMessage(payload, 'Could not send a code. Try again.'));
        return;
      }
      // Deliberately shown even when the address has no account: the backend answers identically either
      // way, and a UI that revealed the difference would undo that on its behalf.
      setCodePurpose('login');
      setUnverifiedEmail(email.trim());
      setShowOtpVerification(true);
    } catch {
      setError('Could not reach the server. Check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  /** Redeem a sign-in code.
   *
   * Goes through `loginWithCode` on the auth context, NOT a local fetch. The local fetch set the session
   * cookie and called `onSuccess`, but never told AuthContext, so the route guard saw no user and bounced
   * straight back to /auth — until a reload re-mounted the provider. Reported on DEV as "kep redirecting to
   * auth page than i reloaded and finally got in". Same shape as `handleSubmit` below, on purpose.
   */
  const handleLoginWithCode = async (code: string) => {
    setError(null);
    setIsLoading(true);
    try {
      const result = await loginWithCode(unverifiedEmail!, code);
      if (result.redirect) {
        // A full page load, not a router navigate: the marketplace has its own auth context.
        window.location.href = result.redirect;
        return;
      }
      if (!result.success) {
        // Stay on the code screen. Bouncing someone back to the password form after a mistyped digit is
        // how a person at a booth gives up.
        setError(result.message);
        return;
      }
      onSuccess(unverifiedEmail!);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpVerify = async (otpCode: string) => {
    setError(null);
    setIsLoading(true);
    
    try {
      const result = await verifyOtp(unverifiedEmail!, otpCode);
      if (result.success) {
        onSuccess(unverifiedEmail!);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('An unexpected error occurred during verification');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || ''}/resend-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: unverifiedEmail }),
        credentials: 'include'
      });

      if (!response.ok) {
        const data = await response.json();
        setError(apiErrorMessage(data, 'Failed to resend verification code'));
      }
    } catch (err) {
      setError('Failed to resend verification code');
    }
  };

  if (showOtpVerification && unverifiedEmail) {
    return (
      <OtpVerification
        email={unverifiedEmail}
        purpose={codePurpose}
        onVerify={codePurpose === 'login' ? handleLoginWithCode : handleOtpVerify}
        onResend={codePurpose === 'login' ? handleRequestLoginCode : handleResendOtp}
        onBack={() => {
          setShowOtpVerification(false);
          setUnverifiedEmail(null);
          setError(null);
          setCodePurpose('signup');
        }}
        isLoading={isLoading}
        error={error}
      />
    );
  }

  return (
    <Card className="w-full max-w-md mx-auto p-8">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Welcome Back</h1>
        <p className="text-gray-600">Sign in to your account to continue</p>
      </div>
      
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-start">
          <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
          <span>{typeof error === 'string' ? error : 'Something went wrong. Please try again.'}</span>
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-gray-400" />
              </div>
              <input
                id="email"
                name="email"
                type="email"
                required
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </div>
          
          <div>
            <div className="flex items-center justify-between mb-1">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <a
                href="/reset-password"
                className="text-sm font-medium text-[#07E5D1] hover:text-[#06D4C2] transition-colors"
              >
                Forgot password?
              </a>
            </div>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <KeyRound className="h-5 w-5 text-gray-400" />
              </div>
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                required
                className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? UI_TEXT.BUTTONS.HIDE_PASSWORD : UI_TEXT.BUTTONS.SHOW_PASSWORD}
                aria-pressed={showPassword}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                )}
              </button>
            </div>
          </div>
          
          <Button 
            type="submit" 
            fullWidth 
            disabled={isLoading}
            icon={isLoading ? (
              <span className="inline-block h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
            ) : (
              <LogIn className="h-5 w-5" />
            )}
          >
            {isLoading ? 'Signing in...' : 'Sign in'}
          </Button>

          {/* The passwordless path, BESIDE the password rather than instead of it. Additive by design:
              an auth change that replaces the existing path locks every account out at once on deploy
              day. Someone who has never set a password — every account created for them — uses this. */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center" aria-hidden="true">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-2 text-xs text-gray-500">or</span>
            </div>
          </div>

          <button
            type="button"
            onClick={handleRequestLoginCode}
            disabled={isLoading}
            className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <Mail className="h-5 w-5 text-gray-400" />
            Email me a sign-in code
          </button>
        </div>
      </form>
      
      <div className="mt-8 text-center">
        <p className="text-sm text-gray-600">
          Don't have an account?{' '}
          <button 
            onClick={onSwitch} 
            className="font-medium text-blue-600 hover:text-blue-500"
          >
            Book a Meeting
          </button>
        </p>
      </div>
    </Card>
  );
};

export default Login;